import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'
import { useRef, useEffect } from 'react'
import * as d3 from 'd3'

// components
import ClusterPopup from '@aytm/app/stats/page/question-views/hm/components/chart/components/cluster-popup/ClusterPopup'
import CommentsPopup from '@aytm/app/stats/page/question-views/hm/components/chart/components/comments-popup/CommentsPopup'
import Lasso from '@aytm/app/stats/page/question-views/hm/components/chart/components/lasso/Lasso'
import ActionButtons from '@aytm/app/stats/page/question-views/hm/components/chart/components/action-buttons/ActionButtons'
import Preloader from '@aytm/app/stats/page/question-views/components/Preloader'

// internal
import { VIEW_MODES } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/constants'
import { drawView, drawClusters } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/render'
import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { MEDIA_INSET } from '@aytm/app/stats/page/question-views/components/lib/constants'
import useClusterPopup from '@aytm/app/stats/page/question-views/hm/components/chart/hooks/useClusterPopup'
import useDotsPopup from '@aytm/app/stats/page/question-views/hm/components/chart/hooks/useDotsPopup'
import useImageScale from '@aytm/app/stats/page/question-views/hm/components/chart/hooks/useImageScale'

// styles
import style from '@aytm/app/stats/page/question-views/hm/components/chart/styles/Chart.module.scss'
import { CLUSTER_FORM_MODE } from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'

// The component stylesheet is linked for screen only, so the stacking that lays the overlay on the
// picture rides inline as well: without it a printed page draws the overlay beside the image.
const PRINT_SAFE = {
  frame: { position: 'relative' },
  clip: { position: 'relative', overflow: 'hidden' },
  image: { display: 'block' },
  overlay: { position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }
}
const DEFAULT_WIDTH = 814

const HeatmapChart = ({
  id,
  image,
  loading,
  answer,
  viewMode,
  clustersMode,
  clusterFormMode,
  changeClustersForm,
  clusters,
  batches,
  config,
  activeClusterId,
  userRoles,
  lassoCluster,
  onCommentsClick,
  onLassoChange,
  onTipChange,
  questionKey,
  apiRef
}) => {
  const svgContainerRef = useRef(null)
  const { popupData, handleShowClusterPopup, handleClusterMouseLeave, handlePopupMouseEnter, handlePopupMouseLeave } =
    useClusterPopup()

  const { dotsPopupData, handleDotMouseOver, handleDotMouseOut, handleDotsPopupMouseEnter, handleDotsPopupMouseLeave } =
    useDotsPopup()

  const { width: containerWidth, componentRef } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  // the picture stands off the block edge by the media inset, so it is scaled to the room
  // inside that inset, not to the full column
  const { imageWidth, imageHeight, scale, scaledWidth, scaledHeight } = useImageScale(
    image,
    containerWidth - 2 * MEDIA_INSET
  )

  const showClusters = clustersMode !== 'clusters-off' && clusters?.length > 0

  useEffect(() => {
    if (!imageWidth || !imageHeight || !svgContainerRef.current) return

    const container = d3.select(svgContainerRef.current)

    // the container holds nothing but what is drawn here, so it is cleared whole
    container.selectAll('svg').remove()

    if (viewMode !== VIEW_MODES.ORIGINAL) {
      drawView(container, {
        id,
        answer,
        viewMode,
        image,
        scale,
        batches,
        config,
        width: imageWidth,
        height: imageHeight,
        onDotMouseOver: handleDotMouseOver,
        onDotMouseOut: handleDotMouseOut
      })
    }

    if (showClusters) {
      drawClusters(container, {
        id,
        clusters,
        image,
        scale,
        activeClusterId,
        clusterFormMode,
        width: imageWidth,
        height: imageHeight,
        onShowClusterPopup: handleShowClusterPopup,
        onClusterMouseLeave: handleClusterMouseLeave
      })
    }
  }, [
    id,
    answer,
    viewMode,
    image,
    scale,
    batches,
    config,
    clusters,
    activeClusterId,
    clusterFormMode,
    showClusters,
    imageWidth,
    imageHeight,
    handleShowClusterPopup,
    handleClusterMouseLeave,
    handleDotMouseOver,
    handleDotMouseOut
  ])

  const handleEditCluster = uid => {
    changeClustersForm(CLUSTER_FORM_MODE.EDIT, uid)
  }

  if (!image?.original) {
    return null
  }

  // TODO replace by getImageAssetUrl
  // const imageUrl = getImageAssetUrl(image.original.path)
  const imageUrl = image.original.url

  return (
    <div ref={componentRef} className={style.chart}>
      <ActionButtons
        id={id}
        image={image}
        viewMode={viewMode}
        svgContainerRef={svgContainerRef}
        showClusters={showClusters}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        questionKey={questionKey}
        apiRef={apiRef}
      />

      {/* the popups sit beside the clipping wrapper, so its rounded frame cannot cut a pin's popup at the edge */}
      <div
        data-testid="stat-hm-image-frame"
        className={classNames(style.frame, { [style.fullsized]: image.fullSize === 1 })}
        style={PRINT_SAFE.frame}
      >
        <div
          id={`hm-view-${id}`}
          data-testid="stat-hm-image-clip"
          className={classNames(style.imageWrapper, style[`viewMode-${viewMode}`])}
          style={{ ...PRINT_SAFE.clip, width: scaledWidth, height: scaledHeight }}
        >
          <img
            className={style.image}
            style={PRINT_SAFE.image}
            width={scaledWidth}
            height={scaledHeight}
            src={imageUrl}
            alt=""
          />

          <div
            data-testid="stat-hm-image-overlay"
            ref={svgContainerRef}
            className={style.svgContainer}
            style={PRINT_SAFE.overlay}
          />

          {clusterFormMode !== CLUSTER_FORM_MODE.OFF && lassoCluster && (
            <Lasso
              cluster={lassoCluster}
              image={image}
              containerWidth={containerWidth}
              answer={answer}
              onLassoChange={onLassoChange}
              onTipChange={onTipChange}
            />
          )}
        </div>

        {dotsPopupData && (
          <CommentsPopup
            comments={dotsPopupData.comments}
            coords={dotsPopupData.coords}
            image={image}
            scale={scale}
            onMouseEnter={handleDotsPopupMouseEnter}
            onMouseLeave={handleDotsPopupMouseLeave}
            onCommentsClick={onCommentsClick}
          />
        )}

        {popupData && (
          <ClusterPopup
            id={id}
            cluster={popupData.cluster}
            clusterEl={popupData.clusterEl}
            image={image}
            scale={scale}
            config={config}
            batches={batches}
            answer={answer}
            userRoles={userRoles}
            onEditCluster={handleEditCluster}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
            onCommentsClick={onCommentsClick}
          />
        )}
      </div>

      {loading ? <Preloader /> : null}
    </div>
  )
}

HeatmapChart.displayName = 'HeatmapChart'

HeatmapChart.propTypes = {
  id: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  image: PropTypes.shape({
    original: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      url: PropTypes.string,
      path: PropTypes.string
    }),
    fullSize: PropTypes.number
  }),
  answer: answerPropType,
  viewMode: PropTypes.string,
  clustersMode: PropTypes.string,
  clusterFormMode: PropTypes.string,
  clusters: PropTypes.array,
  batches: PropTypes.array,
  config: PropTypes.object,
  activeClusterId: PropTypes.string,
  userRoles: PropTypes.array,
  lassoCluster: PropTypes.shape({
    color: PropTypes.string,
    border: PropTypes.array
  }),
  onCommentsClick: PropTypes.func,
  onLassoChange: PropTypes.func,
  onTipChange: PropTypes.func,
  questionKey: PropTypes.string,
  apiRef: PropTypes.object
}

export default HeatmapChart
